import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { fabric } from 'fabric';
import RectIcon from '../images/Icon20_Rectangle.svg'; // zameni sa stvarnim path-om ikonice
import {
  setCursorTooltip,
  updateTool,
} from '../../../redux/actions/canvas_editor';
import Button from './Toolbar/Button';
import { connect } from 'react-redux';
import {
  getThicknessPx,
  hexToRgba,
  getDrawingBounds,
} from '../helpers/fabricHelpers';

import { __ } from '../../../core/utils';

const Rect = ({
  canvas_editor: { viewer, color, selectedThickness, fabricCanvases },
  source: { width, height },
  sourceType,
  isActive,
  user_id,
  user_full_name,
}) => {
  const [state, setState] = useState({
    rect: null,
    isMouseDown: false,
    origX: null,
    origY: null,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (!isActive || !fabricCanvases) return;

    const disposers = [];

    setCursorTooltip(__('canvas.tooltip.shape.start'));

    Object.entries(fabricCanvases).forEach(([pageNumber, canvas]) => {
      canvas.selection = false;
      canvas.defaultCursor = 'crosshair';

      canvas.getObjects().forEach((obj) => {
        obj.selectable = false;
        obj.evented = false;
      });

      function onMouseDown(options) {
        if (options.target || !isActive) return;

        const rawPointer = canvas.getPointer(options.e);
        const bounds = getDrawingBounds(canvas, { width, height }, sourceType);
        const pointer = {
          x: Math.max(0, Math.min(bounds.width, rawPointer.x)),
          y: Math.max(0, Math.min(bounds.height, rawPointer.y)),
        };
        const pdfScaleFactor =
          sourceType === 'pdf' ? canvas?.pdfScaleFactor || 1 : 1;
        const strokeWidth =
          getThicknessPx(selectedThickness.key) * pdfScaleFactor;

        const origX = pointer.x;
        const origY = pointer.y;

        const rect = new fabric.Rect({
          left: origX,
          top: origY,
          width: 1,
          height: 1,
          originX: 'left',
          originY: 'top',
          fill: hexToRgba(color.hex, 0.05),
          strokeWidth,
          stroke: color.hex,
          strokeUniform: true,
          objectType: 'rectangle',
          angle: 0,
          perPixelTargetFind: true,
          selectable: false,
          hasControls: false,
          hasBorders: false,
          userID: user_id,
          userFullName: user_full_name,
          pageNumber,
        });

        canvas.add(rect);

        setState({
          rect,
          isMouseDown: true,
          origX,
          origY,
        });
      }

      function onMouseMove(options) {
        if (
          !isActive ||
          !stateRef.current.isMouseDown ||
          !stateRef.current.rect
        )
          return;

        const rawPointer = canvas.getPointer(options.e);
        const bounds = getDrawingBounds(canvas, { width, height }, sourceType);
        const pointer = {
          x: Math.max(0, Math.min(bounds.width, rawPointer.x)),
          y: Math.max(0, Math.min(bounds.height, rawPointer.y)),
        };

        const rect = stateRef.current.rect;

        let left = Math.min(pointer.x, stateRef.current.origX);
        let top = Math.min(pointer.y, stateRef.current.origY);
        let rectWidth = Math.abs(pointer.x - stateRef.current.origX);
        let rectHeight = Math.abs(pointer.y - stateRef.current.origY);
        const strokeWidth = rect.strokeWidth || 0;

        if (rectWidth > strokeWidth) {
          left += strokeWidth / 2;
          rectWidth -= strokeWidth * 2;
        }

        if (rectHeight > strokeWidth) {
          top += strokeWidth / 2;
          rectHeight -= strokeWidth * 2;
        }

        rect.set({
          left,
          top,
          width: Math.max(0, rectWidth),
          height: Math.max(0, rectHeight),
        });

        canvas.requestRenderAll();

        setCursorTooltip(__('canvas.tooltip.shape.drawing'));
      }

      function onMouseUp() {
        if (!isActive || !stateRef.current.rect) return;

        viewer?.setMouseNavEnabled(true);

        setState({
          rect: null,
          isMouseDown: false,
          origX: null,
          origY: null,
        });

        updateTool({ tool: 'hand' });

        // setCursorTooltip(__('canvas.tooltip.shape.start'));
      }

      const onPointerDown = (e) => {
        viewer?.setMouseNavEnabled(false);
      };

      canvas.upperCanvasEl.addEventListener('pointerdown', onPointerDown);

      canvas.on('mouse:down', onMouseDown);
      canvas.on('mouse:move', onMouseMove);
      canvas.on('mouse:up', onMouseUp);

      disposers.push(() => {
        if (canvas.upperCanvasEl) {
          canvas.upperCanvasEl.removeEventListener(
            'pointerdown',
            onPointerDown
          );
        }

        canvas.off('mouse:down', onMouseDown);
        canvas.off('mouse:move', onMouseMove);
        canvas.off('mouse:up', onMouseUp);
      });
    });

    return () => {
      disposers.forEach((dispose) => dispose());
    };
  }, [
    isActive,
    user_id,
    viewer,
    color,
    selectedThickness,
    fabricCanvases,
    width,
    height,
    sourceType,
  ]);

  const toggleTool = () => {
    updateTool({ tool: 'rect' });
  };

  return (
    <Button
      id='rect'
      label={__('canvas.toolbar.rect')}
      icon={RectIcon}
      active={isActive}
      onClick={toggleTool}
    />
  );
};

Rect.propTypes = {
  isActive: PropTypes.bool.isRequired,
  canvas_editor: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  canvas_editor: state.canvas_editor,
  user_id: state.auth.id,
  user_full_name: state.auth.firstname + ' ' + state.auth.lastname,
});

export default connect(mapStateToProps, null)(Rect);
